/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button, { KINDS } from '@medflex/components/stories/Button';
import Spinner from '@medflex/components/stories/Spinner';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WEEKDAYS } from '@medflex/utils/constants';
import { Switch, SwitchStatusLabel } from '@medflex/components/stories/Switch';
import FileInput from '@medflex/components/stories/FileUpload/FileInput';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { EVENTS, triggerToast } from '@medflex/components/stories/Toast';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { FileItem } from '@medflex/components/stories/FileUpload';
import config from '@medflex/utils/config';
import InputRadio from '@medflex/components/stories/InputRadio';
import Textarea from 'react-textarea-autosize';
import TextSnippetsPopup from '@medflex/components/stories/TextSnippetsPopup';
import { TextSnippetPopupKinds } from '@medflex/components/stories/TextSnippetsPopup/TextSnippetsPopup';
import EditWeekday from './EditWeekday';
import { useFileInput } from '../../../../hooks/useFileInput';
import useSnippets from '../../../../hooks/useSnippets';

export const dayByIndex = index =>
  [
    WEEKDAYS.MONDAY,
    WEEKDAYS.TUESDAY,
    WEEKDAYS.WEDNESDAY,
    WEEKDAYS.THURSDAY,
    WEEKDAYS.FRIDAY,
    WEEKDAYS.SATURDAY,
    WEEKDAYS.SUNDAY,
  ][index] || '';

function isStartTimeBeforeEndTime() {
  const { startTime, endTime } = this.parent;
  if (startTime && endTime) {
    const startDate = new Date(`1970-01-01T${startTime}`);
    const endDate = new Date(`1970-01-01T${endTime}`);
    const isStartDateAfterEndDate =
      !Number.isNaN(startDate.getTime()) &&
      !Number.isNaN(endDate.getTime()) &&
      startDate.getTime() !== endDate.getTime() &&
      startDate.getTime() > endDate.getTime();

    return !isStartDateAfterEndDate;
  }
  return true;
}

export const OUTPUT_TYPES = {
  TEXT_TO_SPEECH: 'textToSpeech',
  AUDIO_FILE_UPLOAD: 'audioFileUpload',
};

const PhoneOpeningHoursEdit = ({ defaultValues, onSubmit, onCancel }) => {
  const { t } = useTranslation('physician', {
    keyPrefix: 'InquiryManagementModule.PhoneOpeningHours.form',
  });
  const { snippets } = useSnippets();
  const dropTarget = useRef(null);
  const [existingFiles, setExistingFiles] = useState(defaultValues?.files || []);
  const [submitting, setSubmitting] = useState(false);
  const [selectionStart, setSelectionStart] = useState();
  const [selectionEnd, setSelectionEnd] = useState();
  const textRef = useRef(null);

  const saveSelection = () => {
    if (textRef.current) {
      setSelectionStart(textRef.current.selectionStart);
      setSelectionEnd(textRef.current.selectionEnd);
    }
  };

  const formSchema = yup.object().shape({
    active: yup.boolean(),
    outputType: yup.string().required(),
    textToSpeech: yup.string().when('outputType', {
      is: OUTPUT_TYPES.TEXT_TO_SPEECH,
      then: yup.string().required(t('errors.text_required')),
    }),
    weekdays: yup.array().when('active', {
      is: true,
      then: yup.array().of(
        yup.object().shape({
          active: yup.boolean(),
          slots: yup.array().when('active', {
            is: true,
            then: yup.array().of(
              yup.object().shape({
                startTime: yup
                  .string()
                  .matches(/(\d){2}:(\d){2}/, t('errors.time_pattern'))
                  .required(t('errors.time_required'))
                  .test('', t('errors.start_time_greater_end_time'), isStartTimeBeforeEndTime),
                endTime: yup
                  .string()
                  .matches(/(\d){2}:(\d){2}/, t('errors.time_pattern'))
                  .required(t('errors.time_required'))
                  .test('', t('errors.end_time_smaller_start_time'), isStartTimeBeforeEndTime),
              }),
            ),
          }),
        }),
      ),
    }),
  });

  const formMethods = useForm({
    defaultValues,
    resolver: yupResolver(formSchema),
    mode: 'onChange',
  });

  const { handleSubmit: wrapSubmit, formState, control, watch, getValues, setValue } = formMethods;
  const { fields: weekdays } = useFieldArray({
    control,
    name: 'weekdays',
  });
  const openingHoursActive = watch('active');
  const textToSpeech = watch('textToSpeech');
  const outputType = watch('outputType');

  const handleSubmit = useCallback(
    formData => {
      let { weekdays } = formData;
      if (!formData.active) {
        weekdays = defaultValues.weekdays;
      }

      onSubmit({
        ...formData,
        weekdays,
      });
    },
    [onSubmit, defaultValues.weekdays],
  );

  const handleFileUploadError = () => {
    triggerToast(EVENTS.ALERT, {
      title: t('toasts.upload_error'),
      options: {
        toastId: 'uploadError',
      },
    });
    setSubmitting(false);
  };

  const handleFileUploadSuccess = () => {
    const formValues = getValues();
    handleSubmit({
      ...formValues,
      // eslint-disable-next-line no-use-before-define
      files,
    });
  };

  const { registerFileInput, removeFile, files, upload, uploading } = useFileInput({
    endpoint: `${config.TA_API_ENDPOINT}/uploads`,
    dropTarget,
    restrictions: {
      maxTotalFileSize: 20 * 1024 * 1024,
      maxNumberOfFiles: 1,
      allowedFileTypes: ['audio/mpeg'],
    },
    onSuccess: handleFileUploadSuccess,
    onError: handleFileUploadError,
  });

  const submitRequest = async formData => {
    setSubmitting(true);
    if (formData.outputType === OUTPUT_TYPES.TEXT_TO_SPEECH) {
      handleSubmit(formData);
      return;
    }
    if (!files.length) {
      handleSubmit({ ...formData, files: existingFiles });
      return;
    }
    upload();
  };

  const handleInsertSnippet = ({ id }) => {
    const selectedSnippet = snippets?.find(sn => sn.id === id);
    if (!selectedSnippet) return;
    const { text } = selectedSnippet;
    if (text) {
      setValue(
        'textToSpeech',
        `${textToSpeech.slice(0, selectionStart)}${text}${textToSpeech.slice(selectionEnd, textToSpeech.length)}`,
        { shouldValidate: true },
      );
      if (textRef.current && selectionStart) {
        setTimeout(() => {
          textRef.current?.setSelectionRange(
            selectionStart + text.length,
            selectionStart + text.length,
          );
          textRef.current?.focus();
        });
      }
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={wrapSubmit(submitRequest)} className="flex flex-col space-y-6">
        <label
          className="flex pt-2 w-full space-x-3 justify-between"
          htmlFor="InquiryManagementModule_inquiryPhoneSettings_openingHours_active"
        >
          <div className="font-semibold">{t('openingHours_active')}</div>
          <Controller
            control={control}
            name="active"
            render={({ onChange, value, name }) => (
              <Switch
                checked={value || false}
                name={name}
                label={t('openingHours_active')}
                onChange={onChange}
                statusLabel={
                  <SwitchStatusLabel enabled={openingHoursActive} className="hidden lg:inline" />
                }
              />
            )}
          />
        </label>

        <div className={`flex flex-col space-y-6 ${openingHoursActive ? 'block' : 'hidden'}`}>
          <p className="text-slate-600 text-sm">{t('timeslots_hint')}</p>
          <div className="flex flex-col space-y-3">
            {weekdays.map((weekday, index) => (
              <fieldset
                key={weekday.id}
                className="flex flex-col sm:flex-row items-start p-3 space-y-6 sm:space-y-0 space-x-4"
              >
                <label
                  className="flex space-x-3 pt-2"
                  htmlFor={`InquiryManagementModule_inquiryPhoneSettings_openingHours_${index}_active`}
                >
                  <Controller
                    control={control}
                    name={`weekdays[${index}].active`}
                    defaultValue={weekday.active}
                    render={({ onChange, value, name }) => (
                      <Switch
                        checked={value || false}
                        name={name}
                        label={t(`weekdays.${dayByIndex(index)}`)}
                        onChange={onChange}
                      />
                    )}
                  />
                  <div className="font-semibold w-26">{t(`weekdays.${dayByIndex(index)}`)}</div>
                </label>

                <EditWeekday key={weekday} weekdayIndex={index} />
              </fieldset>
            ))}
          </div>
          <p className="text-sm">{t('timezone_hint')}</p>

          <div className="flex flex-col items-start pt-2 w-full space-y-2 pr-6">
            <h3 className="font-semibold">{t('outputType.label')}</h3>
            <Controller
              control={control}
              name="outputType"
              defaultValue={outputType}
              render={({ name: outputType, onChange, value: selectedValue }) => (
                <div className="flex flex-col space-y-3 w-full">
                  {Object.values(OUTPUT_TYPES).map(type => (
                    <label className="flex space-x-3" key={type}>
                      <InputRadio
                        name={outputType}
                        checked={selectedValue === type}
                        value={type}
                        onChange={val => {
                          onChange(val);
                        }}
                      />
                      <div className="flex flex-col space-y-3 w-full">
                        <span>{t(`outputType.options.${type}`)}</span>
                        {selectedValue === type && type === OUTPUT_TYPES.TEXT_TO_SPEECH && (
                          <div className="group outline-none p-4 rounded w-full border-slate-300 border-dashed border flex flex-col items-start space-y-2 justify-center bg-white">
                            <Controller
                              control={control}
                              name="textToSpeech"
                              defaultValue={textToSpeech || ''}
                              render={({ name, onChange, value }) => (
                                <Textarea
                                  className="p-3 resize-none border border-slate-500 focus:border-blue-500 rounded outline-none placeholder-slate-500 leading-tight w-full min-h-20"
                                  id={name}
                                  minRows={2}
                                  name={name}
                                  onChange={val => onChange(val)}
                                  value={value}
                                  ref={textRef}
                                  onBlur={saveSelection}
                                />
                              )}
                            />
                            <TextSnippetsPopup
                              snippets={snippets}
                              onClick={handleInsertSnippet}
                              kind={TextSnippetPopupKinds.TEXT_ICON}
                              buttonClassName="flex items-center space-x-2 whitespace-nowrap px-6"
                            />
                          </div>
                        )}
                        {selectedValue === type && type === OUTPUT_TYPES.AUDIO_FILE_UPLOAD && (
                          <div className="flex flex-col space-y-2 w-full">
                            {files.length > 0 && (
                              <FileItem
                                type={files[0].type}
                                name={files[0].name}
                                size={files[0].size}
                                percentage={files[0].progress && files[0].progress.percentage}
                                hasError={!!files[0].error}
                                onDelete={() => removeFile({ id: files[0].id })}
                                small
                              />
                            )}
                            {existingFiles.length > 0 && (
                              <FileItem
                                type={existingFiles[0].type}
                                name={existingFiles[0].name}
                                size={existingFiles[0].size}
                                onDelete={() =>
                                  setExistingFiles(
                                    [...existingFiles].filter(
                                      file => file.id !== existingFiles[0].id,
                                    ),
                                  )
                                }
                                small
                              />
                            )}
                            {files.length === 0 && existingFiles.length === 0 && (
                              <div ref={dropTarget}>
                                <FileInput {...registerFileInput()} accept=".mp3">
                                  {({ select }) => (
                                    <button
                                      className="group outline-none p-4 rounded w-full border-slate-300 border-dashed border flex flex-col items-center justify-center bg-white hover:border-slate-400 "
                                      onClick={select}
                                      type="button"
                                    >
                                      <div className="flex flex-row items-center space-x-3">
                                        <PaperClipIcon className="h-6 w-6 text-blue-500" />
                                        <div className="flex flex-col space-y-1 items-start group`">
                                          <span className="text-blue-600 group-hover:underline">
                                            {t('audio.upload.label')}
                                          </span>
                                          <span className="text-slate-500 text-sm group-hover:underline-slate-500">
                                            {t('audio.upload.hint')}
                                          </span>
                                        </div>
                                      </div>
                                    </button>
                                  )}
                                </FileInput>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            />
          </div>
        </div>

        <div className="flex space-x-8 items-center justify-end">
          <Button kind={KINDS.PRIMARY_GHOST} isDisabled={formState.isSubmitting} onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            isDisabled={
              formState.isSubmitting ||
              !formState.isValid ||
              uploading ||
              (!(files.length > 0 || existingFiles.length > 0) &&
                outputType === OUTPUT_TYPES.AUDIO_FILE_UPLOAD) ||
              submitting
            }
          >
            {formState.isSubmitting ? <Spinner /> : t('save')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

PhoneOpeningHoursEdit.propTypes = {
  defaultValues: PropTypes.any,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

PhoneOpeningHoursEdit.defaultProps = {};

export default PhoneOpeningHoursEdit;
